import moment from "moment"
import { formatDateWithoutYear, monthDictionary } from "../DatePicker/DateFormatters"

const getDefaultRawDataXAxisRanges = (startDate) => {
  return [
    { x: startDate + " 00:00:00", y: null },
    { x: startDate + " 01:00:00", y: null },
    { x: startDate + " 02:00:00", y: null },
    { x: startDate + " 03:00:00", y: null },
    { x: startDate + " 04:00:00", y: null },
    { x: startDate + " 05:00:00", y: null },
    { x: startDate + " 06:00:00", y: null },
    { x: startDate + " 07:00:00", y: null },
    { x: startDate + " 08:00:00", y: null },
    { x: startDate + " 09:00:00", y: null },
    { x: startDate + " 10:00:00", y: null },
    { x: startDate + " 11:00:00", y: null },
    { x: startDate + " 12:00:00", y: null },
    { x: startDate + " 13:00:00", y: null },
    { x: startDate + " 14:00:00", y: null },
    { x: startDate + " 15:00:00", y: null },
    { x: startDate + " 16:00:00", y: null },
    { x: startDate + " 17:00:00", y: null },
    { x: startDate + " 18:00:00", y: null },
    { x: startDate + " 19:00:00", y: null },
    { x: startDate + " 20:00:00", y: null },
    { x: startDate + " 21:00:00", y: null },
    { x: startDate + " 22:00:00", y: null },
    { x: startDate + " 23:00:00", y: null },
    { x: startDate + " 23:59:59", y: null }
  ]
}

const getXAxisLabelForWeeklyGranularity = (startDate, endDate, seriesLength, limit=10, getStartDate=false) => {
  if (getStartDate) {
    return formatDateWithoutYear(startDate)
  }
  if (seriesLength > limit) {
    return [
      [formatDateWithoutYear(startDate) +  " -"], 
      [formatDateWithoutYear(endDate)]
    ]
  } else {
    return `${formatDateWithoutYear(
      startDate
    )} - ${formatDateWithoutYear(endDate)}`
  }
}

const calculateDynamicRange = (yMin, yMax, lowerThreshold, upperThreshold) => {
  let maxValue = yMax * 1.01
  let minValue = yMin * 0.99

  let maxValueSlice = Math.ceil(maxValue/5) * 5
  let minValueSlice = Math.floor(minValue/5) * 5

  let min = Math.min(minValueSlice, lowerThreshold) 
  let max = Math.max(maxValueSlice, upperThreshold) 

  return {min, max}
}

const nextDivisibleBy = (num, divideBy=5) => {
  if (!Number.isFinite(num)) {
    return null
  }
  return Math.ceil(num / divideBy) * divideBy;
}

const getXAxisDateString = (startDate, endDate, dataLength, granularity, getStartDate=false) => {
  let x, dateRangeString
  if (granularity === "weekly") {
    x = getXAxisLabelForWeeklyGranularity(new Date(startDate), new Date(endDate), dataLength, 4, getStartDate)
    dateRangeString = moment(startDate).format("DD MMM") + " - " + moment(endDate).format("DD MMM, YYYY")
  } else if (granularity === "monthly") {
    const month = monthDictionary.find(
      (month) =>
        month.name === startDate.split("-")[1]
    )
    x = month.value
    dateRangeString = month.value + " " + moment(startDate).format("YYYY")
  }

  return {x, dateRangeString}
}

const findYAxisPartitionSizeForPositiveAxis = (number, numberOfPartition = 5) => {
  const partitionSize = number > 5000
    ? nextDivisibleBy(number / numberOfPartition, 1000)
    : number > 2500
      ? 1000 
      : number > 1000
        ? 500
        : number > 500
          ? 200
          : number > 100 
            ? 100 
            : 20;
  
  return partitionSize
}

const findYAxisPartitionSizeForNegativeAxis = (number) => {
  const partitionSize = number >= -100 
      ? 20
      : number >= -500
        ? 100
        : number >= -1000 
          ? 500 
          : 20 
  
  return partitionSize
}

const convertChartValueToResolutionTimeFormat = (minutes) => {
  if (!Number.isFinite(minutes)) {
    return minutes
  }
  if (minutes === 0) {
    return 0
  }
  const totalMinutes = parseFloat(minutes); // Ensure the input is treated as a number
  const days = Math.floor(totalMinutes / (24 * 60)); // Calculate days
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60); // Calculate hours

  return `${days}d ${hours.toString().padStart(2, '0')}h`;
}

/**
 * Calculate the appropriate data interval (in seconds) based on the visible time window
 * @param {number} minX - Start timestamp in milliseconds
 * @param {number} maxX - End timestamp in milliseconds
 * @returns {number} Interval in seconds
 */
const calculateIntervalFromTimeWindow = (minX, maxX) => {
  // minX and maxX are timestamps in milliseconds
  const timeWindowMs = maxX - minX;

  // Convert to minutes for easier comparison
  const timeWindowMinutes = timeWindowMs / (1000 * 60);

  // Based on the granularity table:
  // <= 5 minutes -> 1s interval
  if (timeWindowMinutes <= 5) {
    return 1;
  }
  // 5 - 30 minutes -> 5s interval
  else if (timeWindowMinutes > 5 && timeWindowMinutes <= 30) {
    return 5;
  }
  // 30 min - 1 hour -> 10s interval
  else if (timeWindowMinutes > 30 && timeWindowMinutes <= 60) {
    return 10;
  }
  // 1 - 6 hours -> 1m (60s) interval
  else if (timeWindowMinutes > 60 && timeWindowMinutes <= 6 * 60) {
    return 60;
  }
  // 6 - 12 hours -> 2m (120s) interval
  else if (timeWindowMinutes > 6 * 60 && timeWindowMinutes <= 12 * 60) {
    return 120;
  }
  // 12 - 24 hours -> 5m (300s) interval
  else if (timeWindowMinutes > 12 * 60 && timeWindowMinutes <= 24 * 60) {
    return 300;
  }
  // Default fallback for anything larger than 24 hours
  else {
    return 300;
  }
}

/**
 * Convert an ApexCharts x-axis timestamp (from a zoom event) into the
 * "YYYY-MM-DD HH:mm:ss" string the chart data APIs expect.
 *
 * Chart points carry `x` as a string already formatted in the user's selected
 * timezone (see convertUTCtoZone), which ApexCharts parses as browser-local
 * time. The resulting axis timestamps are therefore user-timezone wall clock
 * read in the browser's timezone - not true instants. Formatting them in
 * browser-local time recovers the user-timezone wall clock that the API layer
 * then converts to UTC. Re-projecting them into the user's timezone would
 * subtract the browser offset a second time and shift the requested window
 * (blank chart whenever browser timezone != selected timezone).
 *
 * @param {number} timestamp - Axis timestamp in milliseconds
 * @returns {string} Wall-clock date string in the user's selected timezone
 */
const formatChartTimestampForApi = (timestamp) => {
  return moment(timestamp).format("YYYY-MM-DD HH:mm:ss");
}

export {
  nextDivisibleBy,
  getXAxisDateString,
  getDefaultRawDataXAxisRanges,
  getXAxisLabelForWeeklyGranularity,
  calculateDynamicRange,
  convertChartValueToResolutionTimeFormat,
  findYAxisPartitionSizeForPositiveAxis,
  findYAxisPartitionSizeForNegativeAxis,
  calculateIntervalFromTimeWindow,
  formatChartTimestampForApi
}
